import { responsive } from '@theme/responsive';

class FaqSection extends HTMLElement {
  #initialized = false;
  #faqItems = [];
  #filterTimer = null;

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.searchTerm = '';
    this.isTicking = false;
  }

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#cacheDom();

    this.addEventListener('click', this.onClick);
    this.tablist?.addEventListener('keydown', this.onKeyDown);
    this.searchInput?.addEventListener('input', this.onInput);
    this.searchClear?.addEventListener('click', this.onClear);
    document.addEventListener('scroll', this.onScroll, { passive: true });

    this.initializeTabs();

    const hash = window.location.hash.replace('#', '');
    if (hash && this.tabs.some((tab) => tab.dataset.toggleId === hash)) {
      requestAnimationFrame(() => this.syncActiveTabToViewport());
    } else {
      this.syncActiveTabToViewport();
    }
  }

  disconnectedCallback() {
    if (!this.#initialized) return;
    this.#initialized = false;

    clearTimeout(this.#filterTimer);
    this.#filterTimer = null;

    this.removeEventListener('click', this.onClick);
    this.tablist?.removeEventListener('keydown', this.onKeyDown);
    this.searchInput?.removeEventListener('input', this.onInput);
    this.searchClear?.removeEventListener('click', this.onClear);
    document.removeEventListener('scroll', this.onScroll);

    this.searchInput = null;
    this.searchClear = null;
    this.noResults = [];
    this.tabs = [];
    this.groups = [];
    this.tablist = null;
    this.stickyTabs = null;
    this.#faqItems = [];
    this.searchTerm = '';
    this.isTicking = false;
  }

  onClick(event) {
    const tab = event.target.closest('[data-action="handle-tab-click"]');
    if (tab) {
      this.handleTabClick(tab);
      return;
    }

    const dropdownButton = event.target.closest('[data-faq-item] [data-action="toggle-dropdown"]');
    if (dropdownButton) {
      this.scrollOpenedDropdownIntoView(dropdownButton);
    }
  }

  // On mobile, opening a question auto-closes any sibling above it (handled by the
  // global Dropdown manager), which shifts the layout up and can push the tapped
  // question out of view. Once the expand animation settles, bring its start just
  // below the sticky header.
  scrollOpenedDropdownIntoView(button) {
    if (!responsive.matchesBreakpoint('pocket')) return;

    // The global toggle runs after this delegated handler, so aria-expanded still
    // holds the pre-click value: 'true' means it is about to close — nothing to do.
    if (button.getAttribute('aria-expanded') === 'true') return;

    const content = button.closest('[data-faq-item]')?.querySelector('.Dropdown--Content');
    if (!content) return;

    const scrollToButton = () => {
      const y = button.getBoundingClientRect().top + window.scrollY - this.getStickyOffset();
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    };

    let settled = false;
    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height') return;
      settled = true;
      content.removeEventListener('transitionend', onTransitionEnd);
      scrollToButton();
    };

    content.addEventListener('transitionend', onTransitionEnd);

    // Fallback when no height transition fires (reduced motion, instant open).
    setTimeout(() => {
      if (settled) return;
      content.removeEventListener('transitionend', onTransitionEnd);
      scrollToButton();
    }, 600);
  }

  onKeyDown(event) {
    const tab = event.target.closest('.legal-tab[role="tab"]');
    if (!tab || !this.tablist?.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    if (index === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = index === 0 ? this.tabs.length - 1 : index - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = index === this.tabs.length - 1 ? 0 : index + 1;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    const nextTab = this.tabs[nextIndex];
    if (!nextTab) return;

    this.handleTabClick(nextTab);
    nextTab.focus();
  }

  onScroll() {
    if (this.isTicking) return;
    this.isTicking = true;
    window.requestAnimationFrame(() => {
      this.syncActiveTabToViewport();
      this.isTicking = false;
    });
  }

  onInput(event) {
    this.searchTerm = event.target.value.trim().toLowerCase();
    this.toggleClearButton();

    clearTimeout(this.#filterTimer);

    if (!this.searchTerm) {
      this.filterBySearch();
      return;
    }

    this.#filterTimer = setTimeout(() => this.filterBySearch(), 200);
  }

  onClear() {
    if (!this.searchInput) return;
    this.searchInput.value = '';
    this.searchTerm = '';
    this.toggleClearButton();
    this.filterBySearch();
    this.searchInput.focus();
  }

  initializeTabs() {
    if (!this.tabs.length || !this.groups.length) return;
    const currentHash = window.location.hash.replace('#', '');
    const hashMatch = this.tabs.find((tab) => tab.dataset.toggleId === currentHash);
    const firstTab = hashMatch || this.tabs[0];
    if (!firstTab) return;
    this.setActiveTabById(firstTab.dataset.toggleId);
  }

  handleTabClick(tab) {
    const targetId = tab.dataset.toggleId;
    if (!targetId) return;

    if (this.searchTerm) {
      this.searchInput.value = '';
      this.searchTerm = '';
      this.toggleClearButton();
      this.resetSearchVisibility();
    }

    this.setActiveTabById(targetId);
    this.scrollToGroup(targetId);
    window.history.replaceState(null, '', `#${targetId}`);
  }

  setActiveTabById(targetId) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.toggleId === targetId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.groups.forEach((group) => {
      const isActive = group.dataset.faqGroupId === targetId;
      group.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  scrollToGroup(targetId) {
    const targetGroup = this.groups.find((group) => group.dataset.faqGroupId === targetId);
    if (!targetGroup || targetGroup.getAttribute('aria-hidden') === 'true') {
      return;
    }

    const yOffset = this.getStickyOffset();
    const y = targetGroup.getBoundingClientRect().top + window.scrollY - yOffset;
    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  }

  getStickyOffset() {
    const headerOffset =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announcement-dynamic-height') || '0') +
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-dynamic-height') || '0');
    return headerOffset + this.getStickyTabsHeight() + 24;
  }

  // On mobile the category tab bar is a horizontal strip stuck below the header,
  // so its height has to be cleared too. On desktop it is a vertical sidebar
  // (also sticky) that does not sit above the content, so it must not be counted.
  getStickyTabsHeight() {
    if (!this.stickyTabs || !responsive.matchesBreakpoint('pocket')) return 0;
    if (getComputedStyle(this.stickyTabs).position !== 'sticky') return 0;
    return this.stickyTabs.getBoundingClientRect().height;
  }

  syncActiveTabToViewport() {
    if (!this.tabs.length || !this.groups.length || this.searchTerm) return;

    const checkLine = this.getStickyOffset() + 8;
    let currentGroup = this.groups[0];
    this.groups.forEach((group) => {
      if (group.getAttribute('aria-hidden') === 'true') return;
      if (group.getBoundingClientRect().top <= checkLine) {
        currentGroup = group;
      }
    });
    if (!currentGroup) return;
    this.setActiveTabById(currentGroup.dataset.faqGroupId);
  }

  setNoResultsVisible(isVisible) {
    this.noResults.forEach((el) => {
      el.classList.toggle('is-hidden', !isVisible);
    });
  }

  filterBySearch() {
    if (!this.searchTerm) {
      this.resetSearchVisibility();
      return;
    }

    let hasAnyVisible = false;
    const matchResults = [];

    this.#faqItems.forEach((entry) => {
      const { item, group, questionText, answerText } = entry;
      const matches = questionText.includes(this.searchTerm) || answerText.includes(this.searchTerm);
      matchResults.push({ item, group, matches });
      if (matches) {
        hasAnyVisible = true;
      }
    });

    if (!hasAnyVisible) {
      this.setNoResultsVisible(true);
      this.groups.forEach((group) => {
        group.setAttribute('aria-hidden', 'false');
      });
      this.#faqItems.forEach(({ item }) => {
        item.classList.remove('is-hidden');
      });
      return;
    }

    this.setNoResultsVisible(false);
    this.tabs.forEach((tab) => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });

    this.groups.forEach((group) => {
      group.setAttribute('tabindex', '-1');
    });

    const visibleGroups = new Set();
    matchResults.forEach(({ item, group, matches }) => {
      item.classList.toggle('is-hidden', !matches);
      if (matches) {
        visibleGroups.add(group);
      }
    });

    this.groups.forEach((group) => {
      group.setAttribute('aria-hidden', visibleGroups.has(group) ? 'false' : 'true');
    });
  }

  resetSearchVisibility() {
    this.setNoResultsVisible(false);
    this.groups.forEach((group) => {
      group.setAttribute('aria-hidden', 'false');
    });
    this.#faqItems.forEach(({ item }) => {
      item.classList.remove('is-hidden');
    });

    this.syncActiveTabToViewport();
  }

  #cacheDom() {
    this.searchInput = this.querySelector('[data-faq-search-input]');
    this.searchClear = this.querySelector('[data-faq-search-clear]');
    this.noResults = Array.from(this.querySelectorAll('[data-faq-no-results]'));
    this.tabs = Array.from(this.querySelectorAll('.legal-tab[role="tab"]'));
    this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
    this.tablist = this.querySelector('[role="tablist"]');
    this.stickyTabs = this.querySelector('.legal-tabs');
    this.#faqItems = [];

    this.groups.forEach((group) => {
      group.querySelectorAll('[data-faq-item]').forEach((item) => {
        this.#faqItems.push({
          item,
          group,
          questionText: item.querySelector('.faq-item__question')?.textContent?.toLowerCase() ?? '',
          answerText: item.querySelector('.faq-item__answer-inner')?.textContent?.toLowerCase() ?? '',
        });
      });
    });
  }

  toggleClearButton() {
    if (!this.searchClear) return;
    this.searchClear.classList.toggle('is-hidden', !this.searchTerm);
  }
}

if (!customElements.get('faq-section')) {
  customElements.define('faq-section', FaqSection);
}
